import React, { useCallback, useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { debounce } from "lodash";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { allWallets, KlashaCNYQuotation } from "../../../../redux/slice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../../utils/encryptionAlgorithm";
import { formatCurrencyAmount } from "../../../../../../utils/currency";
import {
  ContentWrapper,
  InputWrapper,
  Error,
  RateBox,
  BalanceExceptionWrapper,
} from "../../../shared/styles";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import InputAndSelect from "../../../../../../components/common/InputAndSelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { ReactComponent as NotifyIcon } from "../../../../../../assets/icons/notify-icon.svg";
import {
  IDR_DATA_SLICE_KEY,
  IDR_PAYMENT_METHODS,
  IDR_SOURCE_CURRENCIES,
} from "../const";
import { IndonesiaFormInputs, IndonesiaStepFormProps } from "../types";

type WalletProps = {
  currency: string;
  availableBalance: number;
};

const getEnabledPaymentMethod = (value?: string) =>
  IDR_PAYMENT_METHODS.find((method) => method.value === value && !method.disabled)?.value as
    | IndonesiaFormInputs["paymentMethod"]
    | undefined;

const IndonesiaAmountForm: React.FC<IndonesiaStepFormProps> = ({ handleNext }) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData: IndonesiaFormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.[IDR_DATA_SLICE_KEY] || {}
  );
  const { keys } = useAppSelector((state) => state.settings);
  const { wallets, loading } = useAppSelector((state) => state.wallets || {});
  const { businessId } = useAppSelector((state) => state.users);

  const [userData, setUserData] = useState<IndonesiaFormInputs>(() => ({
    sourceCurrency: "USD",
    ...asiaPayoutData,
    paymentMethod: getEnabledPaymentMethod(asiaPayoutData?.paymentMethod),
  }));
  const [walletsLoaded, setWalletsLoaded] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<IndonesiaFormInputs>({
    defaultValues: userData,
  });

  const sourceCurrency = userData?.sourceCurrency || "USD";
  const CURRENT_WALLET = ((wallets || []) as WalletProps[]).find(
    (w) => w.currency === sourceCurrency
  );
  const missingSourceWallet = walletsLoaded && !CURRENT_WALLET;

  const handleDataUpdate = useCallback(
    (field: keyof IndonesiaFormInputs, value: any) => {
      setUserData((prev) => ({ ...prev, [field]: value }));
      setValue(field, value);
    },
    [setValue]
  );

  const handleAmountChange = (enteredAmount: string | number) => {
    const numAmount =
      typeof enteredAmount === "number" ? enteredAmount : parseFloat(enteredAmount) || 0;
    handleDataUpdate("amount", numAmount);
  };

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
      dispatch(allWallets({ businessId })).then((action) => {
        if (allWallets.fulfilled.match(action)) {
          setWalletsLoaded(true);
        }
      });
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    if (!CURRENT_WALLET) {
      handleDataUpdate("balanceError", false);
      return;
    }
    const availableBalance = parseFloat(Number(CURRENT_WALLET.availableBalance).toFixed(2));
    const totalAmount = parseFloat((userData?.totalAmount ?? 0).toFixed(2));
    handleDataUpdate("balanceError", availableBalance < totalAmount);
  }, [CURRENT_WALLET, userData?.totalAmount, handleDataUpdate]);

  const debouncedGetQuotation = useCallback(
    debounce(async (currentData: IndonesiaFormInputs, currentBusinessId) => {
      if (!currentData?.amount || !currentData?.paymentMethod || !currentBusinessId) {
        return;
      }

      const activeSourceCurrency = currentData?.sourceCurrency || "USD";
      const data = {
        destinationAmount: currentData.amount,
        sourceCurrency: activeSourceCurrency,
        destinationCurrency: "IDR",
        transferType: "B2B",
        service: "BANK_ACCOUNT",
        fundSource: "CASH",
      };

      try {
        const { encryptedMessage, publicKey } = await encryptPayloadWithMerchantKeyJIT(
          currentBusinessId,
          data
        );
        const action = await dispatch(
          KlashaCNYQuotation({
            message: { message: encryptedMessage },
            token: publicKey,
          })
        );

        const fxRate = Number(action.payload?.fxRate);
        if (KlashaCNYQuotation.fulfilled.match(action) && fxRate > 0) {
          const { fee, sourceAmount, expiration, id, reference } = action.payload;
          setUserData((prev) => ({
            ...prev,
            sourceCurrency: activeSourceCurrency,
            rate: fxRate,
            convertedAmount: Number((currentData.amount ?? 0) / fxRate),
            totalAmount: Number(sourceAmount + fee),
            fee,
            expiration,
            quotationId: id,
            reference,
            sourceAmount,
            rateError: false,
          }));
        } else {
          setUserData((prev) => ({
            ...prev,
            rate: null,
            fee: null,
            totalAmount: 0,
            rateError: true,
          }));
        }
      } catch (err) {
        setUserData((prev) => ({ ...prev, rateError: true }));
      }
    }, 800),
    [dispatch]
  );

  const amount = userData?.amount;
  const paymentMethod = userData?.paymentMethod;

  useEffect(() => {
    if (amount && amount > 0 && paymentMethod && businessId) {
      debouncedGetQuotation({ ...userData, sourceCurrency }, businessId);
    }
  }, [amount, paymentMethod, sourceCurrency, businessId, debouncedGetQuotation]);

  const onSubmit: SubmitHandler<IndonesiaFormInputs> = (data) => {
    dispatch(
      savedAsiaPayoutData({ country: IDR_DATA_SLICE_KEY, data: { ...data, ...userData } })
    );
    handleNext?.();
  };

  const disableButton =
    loading || !isValid || !userData?.rate || userData?.balanceError || missingSourceWallet;

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <CustomSelect
          $width="100%"
          placeholder="Beneficiary country"
          options={[{ label: "Indonesia", value: "ID" }]}
          value="ID"
          lightBorder
          disabled
        />
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="paymentMethod"
          control={control}
          rules={{ required: "Payment method is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              placeholder="Payment method"
              options={IDR_PAYMENT_METHODS}
              onChange={(val) => {
                field.onChange(val);
                handleDataUpdate("paymentMethod", val);
              }}
              value={field.value || undefined}
              lightBorder
            />
          )}
        />
        {errors?.paymentMethod?.message && (
          <Error>{String(errors.paymentMethod.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="amount"
          control={control}
          rules={{ required: "Amount is required" }}
          render={({ field }) => (
            <InputAndSelect
              amountValue={field.value ?? userData?.amount ?? ""}
              width="100%"
              placeholder="Amount"
              options={[{ label: "IDR", value: "IDR" }]}
              selectValue="IDR"
              onAmountChange={(val) => {
                field.onChange(val);
                handleAmountChange(val);
              }}
            />
          )}
        />
        {errors?.amount?.message && <Error>{String(errors.amount.message)}</Error>}
      </InputWrapper>

      {userData?.rate != null && (
        <RateBox>
          <span className="fee-box">
            / <span>{formatCurrencyAmount("", userData?.rate ?? 0, 4)}</span>
          </span>
          <span>Exchange rate</span>
        </RateBox>
      )}

      {userData?.fee != null && (
        <RateBox>
          <span className="fee-box">
            + <span>{formatCurrencyAmount("", userData?.fee ?? 0, 2)}</span>
          </span>
          <span>Processing fee</span>
        </RateBox>
      )}

      {userData?.rateError && (
        <Error>Exchange rate unavailable. Please try again or contact support.</Error>
      )}

      {(userData?.totalAmount ?? 0) > 0 && (
        <div>
          <Controller
            name="sourceCurrency"
            control={control}
            render={({ field }) => (
              <InputAndSelect
                amountValue={userData?.totalAmount?.toFixed(2) ?? ""}
                width="100%"
                placeholder="Total you will be charged"
                options={IDR_SOURCE_CURRENCIES}
                selectValue={field.value || sourceCurrency}
                onSelectChange={(curr) => {
                  field.onChange(curr);
                  handleDataUpdate("sourceCurrency", curr);
                }}
                readOnly
              />
            )}
          />
          {userData?.balanceError && (
            <BalanceExceptionWrapper>
              <NotifyIcon /> <span>Exceeds wallet balance.</span>
              <Link to={`/wallets/${sourceCurrency.toLowerCase()}`}> Fund wallet</Link>
            </BalanceExceptionWrapper>
          )}
        </div>
      )}

      {missingSourceWallet && (
        <BalanceExceptionWrapper>
          <NotifyIcon /> <span>You don&apos;t have a {sourceCurrency} wallet.</span>
          <Link to="/wallets"> Create wallet</Link>
        </BalanceExceptionWrapper>
      )}

      <Button
        label="Continue"
        onClick={handleSubmit(onSubmit)}
        height="48px"
        disabled={disableButton}
      />
    </ContentWrapper>
  );
};

export default IndonesiaAmountForm;
